import { ResultOf } from "gql.tada";
import { cloneElement, ComponentProps, forwardRef, useMemo } from "react";
// eslint-disable-next-line no-restricted-imports
import { Link as RRLink, useLocation } from "react-router";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { useArticleModalState } from "@hooks/useArticleModalState";
import { useGlobalContext } from "@utils/GlobalContext";

/**
 * Legal links sourced from Contentful must never render as insecure http://.
 * Canonicalize known insecure netflix.com URLs (e.g. the corporateinfo link
 * in the Terms of Use / Privacy content) to their canonical HTTPS form.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const canonicalizeInsecureNetflixUrl = (
  href: string | null | undefined
): string => {
  if (!href) return href ?? "";
  if (!/^http:\/\/(www\.)?netflix\.com(\/|$)/i.test(href)) return href;
  const path = href.replace(/^http:\/\/(www\.)?netflix\.com/i, "");
  return `https://www.netflix.com${path || "/"}`;
};

type LinkProps = { noLocalePrefix?: boolean } & (
  | (ComponentProps<"a"> & { href: string; link?: never })
  | (ComponentProps<"a"> & {
      link: ResultOf<typeof LinkFragment>;
      href?: never;
    })
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href: hrefProp, link, noLocalePrefix = false, ...props }, ref) => {
    const { locale } = useGlobalContext();
    const { pathname: locationPathname } = useLocation();
    const { setIsArticleModalOpen } = useArticleModalState();
    // Slug encodes the full path:
    //   starts with '/' → direct navigation link (e.g. "/targeting", "/ad-formats")
    //   no leading '/' → article modal (e.g. "viewing-moods", "my-article")
    // gql.tada infers slug as {} — cast to string | null for type safety
    const slug = link?.slug as string | null | undefined;
    const isArticleModal = !!slug && !slug.startsWith("/");
    const href = useMemo(() => {
      const localePrefix = noLocalePrefix
        ? ""
        : `/${locale.toLocaleLowerCase()}`;
      if (link) {
        if (link.isExternal) return canonicalizeInsecureNetflixUrl(link.url);
        // Article-modal slugs have no leading '/'. Render the canonical modal
        // URL (R-112 / QC-001): /{locale}/{currentPage}?articleModal={slug} —
        // so direct opens, copy-link, no-JS and crawlers reach real content.
        // Normal SPA clicks still open the modal via the onClick handler below.
        if (isArticleModal) {
          const pagePath = locationPathname.startsWith(localePrefix)
            ? locationPathname.slice(localePrefix.length) || "/"
            : locationPathname;
          return `${localePrefix}${pagePath}?articleModal=${slug}`;
        }
        return localePrefix + (slug ?? "#");
      }
      const internalHref = hrefProp?.startsWith("/");
      const resolvedHref =
        (internalHref ? localePrefix : "") +
        (hrefProp === "/homepage" ? "/" : hrefProp ?? "#");
      // External http:// netflix.com links (e.g. corporateinfo in legal text)
      // are upgraded to their canonical HTTPS URL (QC-010).
      return canonicalizeInsecureNetflixUrl(resolvedHref);
    }, [hrefProp, link, slug, noLocalePrefix, locale, locationPathname, isArticleModal]) as string;

    const isInternal = useMemo(() => {
      const url = new URL(
        href,
        `${window.location.protocol}//${window.location.host}`
      );
      return url.host === window.location.host;
    }, [href]);

    if (isInternal) {
      const linkElement = <RRLink {...props} to={href} ref={ref} />;

      if (isArticleModal) {
        return cloneElement(linkElement, {
          onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            props.onClick?.(e);
            if (e.isDefaultPrevented()) return;
            setIsArticleModalOpen(slug ?? "");
            e.preventDefault();
            e.currentTarget.blur();
          },
        });
      } else return linkElement;
    } else {
      return (
        // eslint-disable-next-line react/forbid-elements
        <a
          {...props}
          href={href}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
        />
      );
    }
  }
);
